import React, { useState, useEffect } from "react";
import { Form, Input, Select } from "antd";
import AddDocument from "components/document";
import Loading from "components/drawerLoading";
import DriversInputs from "components/drivers";
import EntryPermission from "components/entryPermission";
import AddTrailersPlates from "components/trailersPlates";
import * as S from "screens/private/lobby/styled/form.styled";
import { CheckinOriginEnum, PlateFormatType as IPlateFormatType, TrailerType } from "store/lobby/lobby-model";
import { DrawerStatusEnum } from "typings/global.types";
import { isDriverLicenseDocumentType } from "utils/documentType";
import { checkPlateFormatType, maxLengthPlateFormatType, validationCPF, validationDriverLicense, validationSpecialCharacters } from "utils/form/validations";
import { getDocumentLabel, validationCharacters } from "utils/functions";
import useTranslation from "utils/hooks/useTranslation";
import { getTrailerTypeOptions } from "../types";

const FormContent = ({
  form,
  formLayout,
  drawerIsLoading,
  onFormLayoutChange,
  saleChannelActive,
  selectedSaleChannelId,
  fieldsCount,
  setFieldsCount,
  salesChannelList,
  autoSizeInputText,
  hasSaleChannel,
  handleChangeSalesChannel,
  handleChangeProcess,
  inboundProcessesList,
  visibleDocuments,
  documentsList,
  eventType,
  visibleViewDrawer,
  checkinRegisterActive,
  documentType,
  visibleDrawerViewMode,
  visibleDrawerCheckinRegister,
  hasEntryPermission,
  lobbyFetchEntryPermissionAsync,
  stepEntryPermissionStatus,
  isLoadingEntryPermission,
  entryPermissionActive,
  lobbyUpdateStepEntryPermissionStatus,
  lobbyUpdateEntryPermissionActive,
  plateFormatType,
  isAfrDocumentsFlow,
}) => {
  const [disabledFields, setDisabledFields] = useState<{ [key: string]: boolean }>({});
  const FLEET_CHANNEL = useTranslation({ id: "fleet_channel" });
  const TRAILER_TYPE = useTranslation({ id: "truck_type" });
  const REQUIRED_FIELD = useTranslation({ id: "required_field" });
  const CUSTOM_PLATE_DESCRIPTION_INFORMATION = useTranslation({ id: "custom_plate_description_information" });
  const BRAZIL_MERCOSUR_PLATE_DESCRIPTION_INFORMATION = useTranslation({ id: "brazil_mercosur_plate_description_information" });
  const MEXICO_PLATE_DESCRIPTION_INFORMATION = useTranslation({ id: "mexico_plate_description_information" });

  const boxTrailer = useTranslation({ id: "box_trailer" });
  const bulkTrailer = useTranslation({ id: "bulk_trailer" });
  const curtainsider = useTranslation({ id: "curtainsider" });
  const lightweightCurtainsider = useTranslation({ id: "lightweight_curtainsider" });
  const flatbed = useTranslation({ id: "flatbed" });
  const tanker = useTranslation({ id: "tanker" });
  const others = useTranslation({ id: "fleet_channel_others" });

  const trailerTypeDescriptionById = {
    [TrailerType.BOX_TRAILER]: boxTrailer,
    [TrailerType.CURTAINSIDER]: curtainsider,
    [TrailerType.FLATBED]: flatbed,
    [TrailerType.LIGHTWEIGHT_CURTAINSIDER]: lightweightCurtainsider,
    [TrailerType.BULK_TRAILER]: bulkTrailer,
    [TrailerType.TANKER]: tanker,
    [TrailerType.OTHER]: others,
  };

  const PLATE_DESCRIPTIONS: Record<IPlateFormatType, string> = {
    0: BRAZIL_MERCOSUR_PLATE_DESCRIPTION_INFORMATION,
    1: CUSTOM_PLATE_DESCRIPTION_INFORMATION,
    2: MEXICO_PLATE_DESCRIPTION_INFORMATION,
  };

  const checkinOrigin = checkinRegisterActive?.checkinOrigin === CheckinOriginEnum.Integration;
  const canEditVehiclePlate = visibleDrawerCheckinRegister && checkinRegisterActive?.canEditVehiclePlate === true;
  const driver1 = checkinRegisterActive?.drivers?.at(0)?.name;
  const cpf1 = checkinRegisterActive?.drivers?.at(0)?.cpf;
  const hasCpf1 = cpf1 && (isDriverLicenseDocumentType(documentType) ? validationDriverLicense.test(cpf1) : validationCPF.test(cpf1));
  const checkIfDriverIsFilled = driver1?.length > 0 && hasCpf1 && visibleDrawerCheckinRegister;

  useEffect(() => {
    if (visibleDrawerViewMode === DrawerStatusEnum.Edit) {
      setTimeout(() => {
        const initialCarrierValue = form.getFieldValue("carrier");
        const initialProcessDescription = form.getFieldValue("processDescription");

        setDisabledFields({
          carrier: checkinOrigin && initialCarrierValue,
          processDescription: checkinOrigin && initialProcessDescription,
        });
      }, 0);
    } else {
      form.resetFields();
    }
  }, [form, checkinOrigin, visibleDrawerViewMode]);

  const validationPlate = checkPlateFormatType(plateFormatType);

  const translateDescriptionInformationPlate = (plateFormatType: IPlateFormatType): string => PLATE_DESCRIPTIONS[plateFormatType] || PLATE_DESCRIPTIONS[0];

  const isFieldInitiallyDisabled = (fieldName: string) => {
    return disabledFields[fieldName];
  };

  return (
    <Loading show={drawerIsLoading}>
      <Form style={S.FormLayout} layout={"vertical"} form={form} initialValues={{ layout: formLayout }} onValuesChange={onFormLayoutChange} name="registers">
        <S.LayoutForm hasEntryPermission={hasEntryPermission && visibleDrawerCheckinRegister}>
          <div className="item">
            <S.FormItem
              name="carrier"
              hasFeedback
              label={`${useTranslation({ id: "shipping_company" })}*`}
              style={S.ShippingCompanyField}
              rules={[
                {
                  required: true,
                  message: REQUIRED_FIELD,
                },
              ]}
            >
              <Input maxLength={60} style={S.InputCarrier} disabled={visibleViewDrawer() || isFieldInitiallyDisabled("carrier")} />
            </S.FormItem>
            <S.FormPlateItem
              label={`${useTranslation({ id: "plate" })}*`}
              tooltip={translateDescriptionInformationPlate(plateFormatType)}
              name="vehiclePlate"
              hasFeedback
              rules={[
                {
                  required: true,
                  message: REQUIRED_FIELD,
                },
                {
                  pattern: validationPlate,
                  message: useTranslation({ id: "invalid_plate" }),
                },
              ]}
            >
              <Input
                maxLength={maxLengthPlateFormatType(plateFormatType)}
                onKeyPress={(e) => {
                  validationCharacters(e, validationSpecialCharacters);
                }}
                style={S.InputCustomPlate(plateFormatType)}
                disabled={visibleViewDrawer() || !canEditVehiclePlate}
              />
            </S.FormPlateItem>
            <S.FormPlateItem
              plateFormatType={plateFormatType}
              label={`${useTranslation({ id: "trailers_plates" })}`}
              tooltip={
                <>
                  {useTranslation({ id: "description_information_add_plates" })}
                  <br />
                  {translateDescriptionInformationPlate(plateFormatType)}
                </>
              }
              hidden={fieldsCount === 0 && visibleDrawerViewMode === DrawerStatusEnum.View}
              rules={[{ required: false }]}
            >
              <Form.List name="trailersPlates" initialValue={[""]}>
                {(fields, { add }, { errors }) => (
                  <AddTrailersPlates
                    fields={fields}
                    setFieldsCount={setFieldsCount}
                    fieldsCount={fieldsCount}
                    errors={errors}
                    add={add}
                    disabled={visibleViewDrawer()}
                    validationPlate={validationPlate}
                    maxLength={maxLengthPlateFormatType(plateFormatType)}
                    plateFormatType={plateFormatType}
                  />
                )}
              </Form.List>
            </S.FormPlateItem>
            <S.Driver>
              <DriversInputs form={form} disabledFields={checkIfDriverIsFilled || visibleViewDrawer()} drivers={checkinRegisterActive?.drivers} documentType={documentType} />
              <S.VehicleInfo>
                {hasSaleChannel && (
                  <S.FormItem
                    name="saleChannelDescription"
                    data-testid="saleChannel"
                    label={`${FLEET_CHANNEL}*`}
                    style={S.SaleChannelFormItem}
                    hasFeedback={false}
                    rules={[{ required: true, message: REQUIRED_FIELD }]}
                  >
                    <Select size="large" style={S.SaleChannelSelect} onChange={handleChangeSalesChannel} data-testid="selectSalesChannel" disabled={visibleViewDrawer()}>
                      {salesChannelList?.length > 0 &&
                        salesChannelList?.map(({ description, id }) => (
                          <Select.Option value={id} key={id}>
                            {description}
                          </Select.Option>
                        ))}
                    </Select>
                  </S.FormItem>
                )}
                <S.FormItem
                  data-testid="trailerType"
                  name="trailerType"
                  label={`${TRAILER_TYPE}*`}
                  style={S.SaleChannelFormItem}
                  hasFeedback={false}
                  rules={[{ required: true, message: REQUIRED_FIELD }]}
                >
                  <Select size="large" style={S.SaleChannelSelect} data-testid="selectTrailerType" disabled={visibleViewDrawer()}>
                    {getTrailerTypeOptions(selectedSaleChannelId, visibleViewDrawer()).map(({ id }) => (
                      <Select.Option value={id} key={id}>
                        {trailerTypeDescriptionById[id]}
                      </Select.Option>
                    ))}
                  </Select>
                </S.FormItem>
              </S.VehicleInfo>
            </S.Driver>
            <S.FormItem
              label={useTranslation({ id: "observation" })}
              name="observation"
              data-testid="observation_field"
              hidden={visibleViewDrawer() && !form.getFieldValue("observation")}
            >
              <Input.TextArea maxLength={500} autoSize={autoSizeInputText} disabled={visibleViewDrawer()} />
            </S.FormItem>
          </div>
          <div className="divider" />
          <div className="item">
            <S.FormItem
              data-testid="process"
              name="processDescription"
              label={`${useTranslation({ id: "processes" })}*`}
              style={S.ProcessesFormItem}
              hasFeedback={false}
              rules={[
                {
                  required: true,
                  message: REQUIRED_FIELD,
                },
              ]}
            >
              <Select
                size="large"
                style={S.ProcessesSelect}
                onChange={handleChangeProcess}
                data-testid="selectProcess"
                disabled={visibleViewDrawer() || isFieldInitiallyDisabled("processDescription")}
              >
                {inboundProcessesList?.length > 0 &&
                  inboundProcessesList?.map(({ description, processId }, index) => (
                    <Select.Option value={processId} key={index}>
                      {description}
                    </Select.Option>
                  ))}
              </Select>
            </S.FormItem>
            {visibleDocuments && (
              <>
                {documentsList?.map((document) => (
                  <AddDocument
                    id={document.id}
                    form={form}
                    eventType={eventType}
                    key={document.id}
                    hidden={false}
                    label={getDocumentLabel(document.description, isAfrDocumentsFlow)}
                    fieldName={document.description}
                    disabled={visibleViewDrawer()}
                    allowLetters={document.maskRules.allowLetters}
                    allowNumbers={document.maskRules.allowNumbers}
                    allowSpecialCharacters={document.maskRules.allowSpecialCharacters}
                    minCharacters={document.maskRules.minCharacters}
                    maxCharacters={document.maskRules.maxCharacters}
                    required={document.required}
                    visibleDrawerViewMode={visibleDrawerViewMode}
                    isIntegration={checkinOrigin}
                  />
                ))}
              </>
            )}
          </div>
          {hasEntryPermission && visibleDrawerCheckinRegister && (
            <EntryPermission
              form={form}
              isLoadingEntryPermission={isLoadingEntryPermission}
              entryPermissionActive={entryPermissionActive}
              stepEntryPermissionStatus={stepEntryPermissionStatus}
              lobbyFetchEntryPermissionAsync={lobbyFetchEntryPermissionAsync}
              documentType={documentType}
              plateFormatType={plateFormatType}
              lobbyUpdateStepEntryPermissionStatus={lobbyUpdateStepEntryPermissionStatus}
              lobbyUpdateEntryPermissionActive={lobbyUpdateEntryPermissionActive}
              saleChannelActive={saleChannelActive}
            />
          )}
          <input data-testid="change-process" onChange={(e) => handleChangeProcess(e.target.value)} style={S.FakeBtnConfirm} />
          <input data-testid="change-sale-channel" onChange={(e) => handleChangeSalesChannel(Number(e.target.value))} style={S.FakeBtnConfirm} />
        </S.LayoutForm>
      </Form>
    </Loading>
  );
};

export default FormContent;
